import { signOut } from "firebase/auth";
import { getFirebaseAuth } from "@/lib/firebaseClient";
import { clearPreRegSession } from "@/app/helpers/preRegSession";

const LOGOUT_FLAG_KEY = "__ael_logout_in_progress_at";
const LOGOUT_FLAG_TTL_MS = 20 * 1000;
const FRONTEND_COOKIE_NAMES = ["__session_front", "__session_front_email"];
const SESSION_STORAGE_PREFIXES_TO_CLEAR = [
  "USERTOFIN_CACHE:",
  "USERTOFIN_ALIAS:",
  "profilCache:",
  "ael_pre_reg_",
];
const SESSION_STORAGE_KEYS_TO_CLEAR = ["__serverTimeOffsetMs"];

function nowMs() {
  return Date.now();
}

function setLogoutFlagStorage(value) {
  try {
    if (typeof window === "undefined") return;
    window.__AEL_LOGOUT_IN_PROGRESS = true;
    window.sessionStorage?.setItem(LOGOUT_FLAG_KEY, value);
    window.localStorage?.setItem(LOGOUT_FLAG_KEY, value);
  } catch {}
}

function clearLogoutFlagStorage() {
  try {
    if (typeof window === "undefined") return;
    window.__AEL_LOGOUT_IN_PROGRESS = false;
    window.sessionStorage?.removeItem(LOGOUT_FLAG_KEY);
    window.localStorage?.removeItem(LOGOUT_FLAG_KEY);
  } catch {}
}

export function markLogoutInProgress() {
  setLogoutFlagStorage(String(nowMs()));
}

export function clearLogoutInProgress() {
  clearLogoutFlagStorage();
}

export function isLogoutInProgress() {
  if (typeof window === "undefined") return false;

  if (window.__AEL_LOGOUT_IN_PROGRESS) {
    return true;
  }

  const readTs = (storage) => {
    try {
      const raw = storage?.getItem(LOGOUT_FLAG_KEY);
      const ts = Number(raw);
      return Number.isFinite(ts) ? ts : null;
    } catch {
      return null;
    }
  };

  const ts = readTs(window.sessionStorage) ?? readTs(window.localStorage);
  if (!ts) return false;

  const active = nowMs() - ts < LOGOUT_FLAG_TTL_MS;
  if (!active) {
    clearLogoutFlagStorage();
  }
  return active;
}

export function clearFrontendSessionCookies() {
  if (typeof document === "undefined") return;
  const secureOptions =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? ["", "; Secure"]
      : [""];

  const expires = "Expires=Thu, 01 Jan 1970 00:00:00 GMT";
  FRONTEND_COOKIE_NAMES.forEach((cookieName) => {
    secureOptions.forEach((secureSuffix) => {
      document.cookie = `${cookieName}=; Path=/; SameSite=Lax; Max-Age=0; ${expires}${secureSuffix}`;
    });
  });
}

export function clearFrontendSessionStorage() {
  if (typeof window === "undefined" || !window.sessionStorage) return;

  try {
    for (let i = window.sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = window.sessionStorage.key(i);
      if (!key) continue;

      if (SESSION_STORAGE_KEYS_TO_CLEAR.includes(key)) {
        window.sessionStorage.removeItem(key);
        continue;
      }

      if (SESSION_STORAGE_PREFIXES_TO_CLEAR.some((prefix) => key.startsWith(prefix))) {
        window.sessionStorage.removeItem(key);
      }
    }
  } catch {}

  clearPreRegSession();
}

export async function runLogout({ api } = {}) {
  markLogoutInProgress();

  try {
    if (api) {
      await fetch(`${api}/login/logout`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        keepalive: true,
      });
    }
  } catch (err) {
    console.warn("[logout] backend logout failed:", err?.message || err);
  }

  clearFrontendSessionCookies();
  clearFrontendSessionStorage();

  try {
    const auth = await getFirebaseAuth();
    await signOut(auth);
  } catch (err) {
    console.warn("[logout] firebase signOut failed:", err?.message || err);
  }
}
